import { motion } from "framer-motion";
import { ArrowUpRight, Award, Star, Heart, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import { SocialLinks } from "@/components/SocialLinks";
import { SiteHeader } from "@/components/SiteHeader";

const ITEMS = [
  {
    icon: Star,
    category: "Recognition",
    title: "Recognized by the LinkedIn community",
    caption: "A moment of recognition and support from peers and the wider professional community.",
    url: "https://www.linkedin.com/posts/rheasharma_linkedin-people-like-activity-7074856029997416448-ebpK",
  },
  {
    icon: Sparkles,
    category: "Feature",
    title: "International Women's Day 2021 feature",
    caption: "Featured for work spanning academics and marketing, celebrated on International Women's Day.",
    url: "https://www.linkedin.com/posts/rheasharma_internationalwomensday2021-academic-marketing-activity-6779294151616847872-u-uK",
  },
  {
    icon: Heart,
    category: "Appreciation",
    title: "Appreciation and credibility",
    caption: "Kind words and appreciation that speak to trust, credibility and impact.",
    url: "https://www.linkedin.com/posts/rheasharma_amazing-appreciate-credibility-activity-6706104653593088000-j3QE",
  },
  {
    icon: Award,
    category: "Story",
    title: "A story worth telling",
    caption: "On finding your voice and sharing the story you carry within.",
    url: "https://www.linkedin.com/posts/rheasharma_there-is-no-greater-agony-than-bearing-an-activity-6705136040933433344-W3pr",
  },
];

export default function Achievements() {
  return (
    <div className="min-h-screen bg-white text-[#041C3E]" data-testid="achievements-page">
      <Seo
        title="Achievements & Recognition | The Next Move by Rhellex"
        description="Recognitions, features and appreciation for Rhea Sharma across her career in Digital Adoption, Learning and Development."
      />
      <SiteHeader />

      <main className="max-w-6xl mx-auto px-6 py-16">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">Achievements &amp; recognition</p>
        <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight font-bold mt-3 max-w-3xl">
          A track record of recognition and impact.
        </h1>
        <p className="text-base md:text-lg text-[#3A4A63] mt-6 max-w-2xl leading-relaxed">
          A selection of features, recognitions and appreciation from across Rhea's career. Each links to the
          original post on LinkedIn.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-12" data-testid="achievements-grid">
          {ITEMS.map(({ icon: Icon, category, title, caption, url }, i) => (
            <motion.a
              key={title}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`achievement-card-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="group bg-white border border-[#E5E7EB] p-8 flex flex-col hover:border-[#041C3E] hover:shadow-lg transition-all duration-200"
            >
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center justify-center h-11 w-11 bg-[#041C3E] text-[#C69A52]">
                  <Icon size={20} />
                </span>
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#9CA3AF]">{category}</span>
              </div>
              <h2 className="font-head text-xl font-bold mt-6 leading-snug">{title}</h2>
              <p className="text-sm text-[#3A4A63] mt-3 leading-relaxed flex-1">{caption}</p>
              <span className="inline-flex items-center gap-2 mt-6 text-sm font-semibold text-[#041C3E] group-hover:text-[#D94833] transition-colors">
                View on LinkedIn <ArrowUpRight size={16} />
              </span>
            </motion.a>
          ))}
        </div>

        <div className="mt-16 flex flex-wrap items-center gap-4">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#041C3E]">Rhea Sharma, PMP® · MBA</span>
          <SocialLinks tone="light" />
        </div>
      </main>
    </div>
  );
}
